import { useState, useEffect } from 'react'
import { useCommenterAuth } from '../hooks/useCommenterAuth'
import { useUserProfile } from '../hooks/useUserProfile'
import { useFileUpload } from '../hooks/useFileUpload'
import { lumi } from '../lib/lumi'
import {User, Mail, CreditCard, LogOut, Upload, Save, X, ArrowLeft} from 'lucide-react'

interface UserProfilePageProps {
  onNavigate: (view: string) => void
  viewingUserId?: string | null  // If provided, view this user's profile (read-only)
}

export function UserProfilePage({ onNavigate, viewingUserId }: UserProfilePageProps) {
  const { user, logout, refreshUser } = useCommenterAuth()
  
  // If viewingUserId is provided, we're viewing someone else's profile
  // Otherwise, we're viewing our own profile
  const targetUserId = viewingUserId || user?.userId || ''
  const isOwnProfile = !viewingUserId || (user && viewingUserId === user.userId)
  
  const { profile, loading: profileLoading, createOrUpdateProfile } = useUserProfile(targetUserId)
  const { uploadFiles, uploading } = useFileUpload()
  
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    nickname: '',
    bio: '',
    profileImageUrl: '',
    bannerImageUrl: '',
    xUrl: '',
    baseAppUrl: '',
    farcasterUrl: ''
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedBanner, setSelectedBanner] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string>('')
  const [saveLoading, setSaveLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  useEffect(() => {
    if (profile) {
      setFormData({
        nickname: profile.nickname || user?.username || 'User',
        bio: profile.bio || '',
        profileImageUrl: profile.profileImageUrl || '',
        bannerImageUrl: profile.bannerImageUrl || '',
        xUrl: profile.xUrl || '',
        baseAppUrl: profile.baseAppUrl || '',
        farcasterUrl: profile.farcasterUrl || ''
      })
      setPreviewUrl(profile.profileImageUrl || '')
      setBannerPreviewUrl(profile.bannerImageUrl || '')
    } else if (user) {
      setFormData({
        nickname: user.nickname || user.username || 'User',
        bio: '',
        profileImageUrl: user.profileImageUrl || '',
        bannerImageUrl: '',
        xUrl: '',
        baseAppUrl: '',
        farcasterUrl: ''
      })
      setPreviewUrl(user.profileImageUrl || '')
      setBannerPreviewUrl('')
    }
  }, [profile, user])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedBanner(file)
      setBannerPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleSave = async () => {
    if (!user) return

    setSaveLoading(true)
    try {
      let profileImageUrl = formData.profileImageUrl

      // Upload new profile image if selected
      if (selectedFile) {
        const results = await uploadFiles([selectedFile])
        if (results && results.length > 0 && results[0].fileUrl) {
          profileImageUrl = results[0].fileUrl
        }
      }

      // Upload new banner image if selected
      let bannerImageUrl = formData.bannerImageUrl
      if (selectedBanner) {
        const results = await uploadFiles([selectedBanner])
        if (results && results.length > 0 && results[0].fileUrl) {
          bannerImageUrl = results[0].fileUrl
        }
      }

      // Update profile
      await createOrUpdateProfile({
        nickname: formData.nickname,
        bio: formData.bio,
        profileImageUrl,
        bannerImageUrl,
        xUrl: formData.xUrl,
        baseAppUrl: formData.baseAppUrl,
        farcasterUrl: formData.farcasterUrl
      })

      // Refresh user data
      await refreshUser()

      setToastMessage('Profile updated successfully!')
      setShowToast(true)
      setEditing(false)
      setSelectedFile(null)
      setSelectedBanner(null)
      
      setTimeout(() => setShowToast(false), 3000)
    } catch (error) {
      console.error('Failed to update profile:', error)
      setToastMessage('Failed to update profile')
      setShowToast(true)
      setTimeout(() => setShowToast(false), 3000)
    } finally {
      setSaveLoading(false)
    }
  }

  const handleManageSubscription = async () => {
    console.log('Subscribe button clicked', { user, hasUser: !!user })
    
    if (!user) {
      console.error('No user found when trying to subscribe')
      setToastMessage('Please log in first')
      setShowToast(true)
      setTimeout(() => setShowToast(false), 3000)
      return
    }

    try {
      console.log('Subscription status:', user.subscriptionStatus)
      
      // Check if user has active subscription
      if (user.subscriptionStatus === 'active' && user.stripeCustomerId) {
        console.log('Redirecting to Stripe portal')
        // Redirect to Stripe Customer Portal
        const portalUrl = `https://billing.stripe.com/p/login/test_00000000000000`
        window.open(portalUrl, '_blank')
      } else {
        console.log('Creating checkout session for commenter subscription')
        // Create checkout session for $5/month commenter plan
        const result = await lumi.functions.invoke('createCheckoutSession', {
          method: 'POST',
          body: {
            userId: user.userId,
            email: user.email,
            planType: 'commenter'
          }
        })

        console.log('Checkout session result:', result)

        if (result.url) {
          console.log('Opening Stripe checkout:', result.url)
          // Open in new tab and redirect after payment
          const win = window.open()
          if (win) {
            win.location.href = result.url
          } else {
            console.error('Failed to open popup window')
            setToastMessage('Please allow popups to complete subscription')
            setShowToast(true)
            setTimeout(() => setShowToast(false), 3000)
          }
        } else {
          console.error('No URL in response:', result)
          setToastMessage('Failed to create checkout session')
          setShowToast(true)
          setTimeout(() => setShowToast(false), 3000)
        }
      }
    } catch (error: any) {
      console.error('Subscription management error:', error)
      console.error('Error details:', {
        message: error?.message,
        data: error?.data,
        stack: error?.stack
      })
      const errorMessage = error?.data?.error || error?.message || 'Failed to manage subscription'
      setToastMessage(errorMessage)
      setShowToast(true)
      setTimeout(() => setShowToast(false), 3000)
    }
  }

  const handleLogout = () => {
    logout()
    onNavigate('home')
  }

  // Allow viewing other profiles without being logged in, but require login for own profile
  if (!viewingUserId && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-slate-400 mb-4">Please log in to view your profile</p>
          <button
            onClick={() => onNavigate('home')}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  // If viewing someone else's profile and no profile found
  if (viewingUserId && !profileLoading && !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-slate-400 mb-4">User profile not found</p>
          <button
            onClick={() => onNavigate('searchUsers')}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            Back to Search Users
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back/Close Button */}
        <button
          onClick={() => onNavigate('home')}
          className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </button>
        {/* Toast Notification */}
        {showToast && (
          <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
            {toastMessage}
          </div>
        )}

        {/* Banner Image */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-blue-500/20 overflow-hidden mb-6">
          <div className="relative h-48 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
            {bannerPreviewUrl ? (
              <img src={bannerPreviewUrl} alt="Banner" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <Upload className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No banner image</p>
                </div>
              </div>
            )}
            {editing && (
              <label className="absolute bottom-4 right-4 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg cursor-pointer shadow-lg transition-colors flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload Banner
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* Profile Header */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-blue-500/20 p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-6">
              {/* Profile Image */}
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-700 border-4 border-blue-500/30">
                  {previewUrl ? (
                    <img src={previewUrl} alt={formData.nickname || 'User profile'} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-12 h-12 text-slate-500" />
                    </div>
                  )}
                </div>
                {editing && (
                  <label className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-500 text-white p-2 rounded-full cursor-pointer shadow-lg transition-colors">
                    <Upload className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* User Info */}
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">
                  {formData.nickname || profile?.nickname || 'User'}
                </h1>
                {isOwnProfile && user && (
                  <>
                    <p className="text-slate-400 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {user.email}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        user.subscriptionStatus === 'active'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-slate-700 text-slate-400 border border-slate-600'
                      }`}>
                        {user.subscriptionStatus === 'active' ? '✓ Subscribed' : 'Free Account'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons - Only show for own profile */}
            <div className="flex gap-2">
              {isOwnProfile && !editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                >
                  Edit Profile
                </button>
              ) : isOwnProfile && editing ? (
                <>
                  <button
                    onClick={() => {
                      setEditing(false)
                      setSelectedFile(null)
                      setSelectedBanner(null)
                      if (profile) {
                        setFormData({
                          nickname: profile.nickname || user?.username || 'User',
                          bio: profile.bio || '',
                          profileImageUrl: profile.profileImageUrl || '',
                          bannerImageUrl: profile.bannerImageUrl || '',
                          xUrl: profile.xUrl || '',
                          baseAppUrl: profile.baseAppUrl || '',
                          farcasterUrl: profile.farcasterUrl || ''
                        })
                        setPreviewUrl(profile.profileImageUrl || '')
                        setBannerPreviewUrl(profile.bannerImageUrl || '')
                      }
                    }}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saveLoading || uploading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {saveLoading || uploading ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : null}
            </div>
          </div>

          {/* Editable Fields */}
          {editing && (
            <div className="space-y-4 pt-6 border-t border-slate-700">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Your display name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Bio
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  placeholder="Tell us about yourself..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  X (Twitter) URL
                </label>
                <input
                  type="url"
                  value={formData.xUrl}
                  onChange={(e) => setFormData({ ...formData, xUrl: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="https://x.com/yourprofile"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Base App URL
                </label>
                <input
                  type="url"
                  value={formData.baseAppUrl}
                  onChange={(e) => setFormData({ ...formData, baseAppUrl: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="https://base.org/yourprofile"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Farcaster URL
                </label>
                <input
                  type="url"
                  value={formData.farcasterUrl}
                  onChange={(e) => setFormData({ ...formData, farcasterUrl: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="https://warpcast.com/yourprofile"
                />
              </div>
            </div>
          )}

          {/* Bio and Links Display (when not editing) */}
          {!editing && (
            <div className="pt-6 border-t border-slate-700 space-y-4">
              {formData.bio && (
                <div>
                  <h3 className="text-sm font-medium text-slate-400 mb-2">Bio</h3>
                  <p className="text-slate-300">{formData.bio}</p>
                </div>
              )}
              
              {(formData.xUrl || formData.baseAppUrl || formData.farcasterUrl) && (
                <div>
                  <h3 className="text-sm font-medium text-slate-400 mb-2">Social Links</h3>
                  <div className="flex flex-wrap gap-3">
                    {formData.xUrl && (
                      <a
                        href={formData.xUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        X.com
                      </a>
                    )}
                    {formData.baseAppUrl && (
                      <a
                        href={formData.baseAppUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Base App
                      </a>
                    )}
                    {formData.farcasterUrl && (
                      <a
                        href={formData.farcasterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Farcaster
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Subscription Management - Only show for own profile */}
        {isOwnProfile && user && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-blue-500/20 p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-blue-400" />
                Subscription
              </h2>
              <p className="text-slate-400 mb-4">
                {user.subscriptionStatus === 'active'
                  ? 'You have access to comment, like, and review features'
                  : 'Subscribe for $5/month to unlock commenting, liking, and review features'}
              </p>
            </div>
            <button
              onClick={handleManageSubscription}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-blue-500/50"
            >
              {user.subscriptionStatus === 'active' ? 'Manage Subscription' : 'Upgrade to Commenter'}
            </button>
          </div>

          {user.subscriptionStatus === 'active' && (
            <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm">
                ✓ Active subscription - You can comment, like, and review projects
              </p>
            </div>
          )}
        </div>
        )}

        {/* Logout - Only show for own profile */}
        {isOwnProfile && user && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-blue-500/20 p-8">
          <button
            onClick={handleLogout}
            className="w-full px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
        )}
      </div>
    </div>
  )
}
